<script setup lang="ts">
    import { useCaseSettingsViewModel } from '@/views/case-settings/composables/useCaseSettingsViewModel';
    import { computed, ref, toRef } from 'vue';
    import { availableStemSystems } from '@/domain/catalog/stemSystems';
    import { alignmentModes } from '@/domain/anatomy/alignmentModes';
    import StemSystemSelection from '@/components/stems/StemSystemSelection.vue';
    import { CASE_SETTINGS_TARGETS } from '@/domain/analytics/analyticsTargets';
    import {
        type GroupableStem,
        useStemSystemGrouping,
    } from '@/domain/catalog/useStemSystemGrouping';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';

    const inclinationOutOfRangeMessage = `Please provide a number between ${MIN_INCLINATION} and ${MAX_INCLINATION}`;
    const anteversionOutOfRangeMessage = `Please provide a number between ${MIN_ANTEVERSION} and ${MAX_ANTEVERSION}`;

    const vm = useCaseSettingsViewModel();

    const isLoading = ref(false);
    const displaySurgeonError = ref(false);

    /**
     * The intention is that the surgeon can be changed even if the surgeon does not have preferences.
     * An error will be shown when if the new surgeon selected does not have preferences
     */
    const onUpdateSurgeon = async (surgeonId: number) => {
        displaySurgeonError.value = true;
        isLoading.value = true;
        await vm.onUpdateSurgeon(surgeonId);
        isLoading.value = false;
    };

    const surgeonErrorMessage = computed((): string => {
        if (displaySurgeonError.value) {
            if (vm.hasFieldError('surgeon')) {
                if (vm.specifications.surgeon.error) {
                    return SURGEON_ERROR_MESSAGE;
                }

                if (vm.specifications.surgeon.selected === null) {
                    return 'Please select a surgeon';
                }
            }
        }

        return '';
    });

    const SURGEON_ERROR_MESSAGE =
        'Selected surgeon does not have saved surgical preferences. Please select a different surgeon.';

    const areSurgeonSpecsDisabled = computed(() => {
        return !vm.isSpecificationsEnabled || vm.hasStudy || vm.isCaseReadonly;
    });

    const availableStems = computed(() => {
        // Show all stem types from all available systems, not just the preferred one
        return vm.specifications.stems.options.filter(
            (object: GroupableStem) => !object.type.includes('Legacy'),
        );
    });

    const stemSystems = computed(() => {
        return availableStemSystems();
    });

    const { stemSystemGroups, selectedStemTypesBySystem, updateSelectedStems } =
        useStemSystemGrouping({
            availableStems,
            selectedStemIds: toRef(vm.specifications.stems, 'selected'),
            stemSystems,
        });

    const hasPreferredSystemError = computed(() => {
        const preferredSystem = vm.specifications.stems.preferredSystem;
        const selectedTypes = selectedStemTypesBySystem.value.get(preferredSystem);
        return !selectedTypes || selectedTypes.length === 0;
    });
</script>

<template>
    <app-form-group
        label="Surgeon"
        input-id="case-surgeon-choices"
        :data-analytics-id="CASE_SETTINGS_TARGETS.selectSurgeon"
    >
        <!-- The error in the surgeon is displayed even if the save button was not clicked yet        -->
        <v-select
            variant="outlined"
            density="compact"
            label="Select a surgeon"
            input-id="case-surgeon-choices"
            :model-value="vm.specifications.surgeon.selected"
            :items="
                vm.specifications.surgeon.options.map(({ name, id }) => ({
                    title: name,
                    value: id,
                }))
            "
            :disabled="vm.hasStudy || vm.isCaseReadonly"
            :error-messages="surgeonErrorMessage"
            data-testid="surgeon-select"
            @update:model-value="onUpdateSurgeon"
        >
        </v-select>
    </app-form-group>
    <app-heading>Stem</app-heading>
    <app-form-group label="Preferred System" :show-label="false" data-testid="preferred-system">
        <stem-system-selection
            :stem-system-groups="stemSystemGroups"
            :selected-stem-types-by-system="selectedStemTypesBySystem"
            :preferred-system="vm.specifications.stems.preferredSystem"
            :stem-systems="stemSystems"
            :disabled="areSurgeonSpecsDisabled"
            :show-preferred-system-error="hasPreferredSystemError"
            @update:preferred-system="vm.specifications.stems.preferredSystem = $event"
            @update:selected-stems="updateSelectedStems"
        />
        <p v-if="vm.showFieldError('preferredSystem')" class="text-error text-caption mt-2">
            Please select an option
        </p>
    </app-form-group>

    <app-form-group
        label="Leg Length Change"
        input-id="target-leg-length-input"
        :data-analytics-id="CASE_SETTINGS_TARGETS.legLengthInput"
    >
        <app-text-input
            v-model="vm.specifications.targets.legLength"
            suffix="mm"
            data-test-id="target-leg-length-input"
            input-id="target-leg-length-input"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('targetLegLengthChange')
                    ? 'Please provide a number between -20 and 20'
                    : ''
            "
        />
    </app-form-group>

    <app-form-group label="Offset Change" input-id="target-offset-input">
        <app-text-input
            v-model="vm.specifications.targets.offset"
            suffix="mm"
            data-test-id="target-offset-input"
            input-id="target-offset-input"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('targetOffsetChange')
                    ? 'Please provide a number between -20 and 20'
                    : ''
            "
        />
    </app-form-group>

    <app-heading>Cup</app-heading>

    <app-form-group label="Inclination Angle" input-id="cup-inclination-angle">
        <app-text-input
            v-model="vm.specifications.cup.inclinationAngle"
            suffix="deg"
            data-test-id="inclination-angle-input"
            input-id="cup-inclination-angle"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('cupInclinationAngle') ? inclinationOutOfRangeMessage : ''
            "
        />
    </app-form-group>

    <app-form-group label="Fitting mode" data-testid="fitting-mode-buttons">
        <app-toggle
            v-model="vm.specifications.cup.fitMethod"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="vm.showFieldError('cupFittingMode') ? 'Please provide an option' : ''"
            :options="[
                { label: 'Auto', value: 'acid' },
                { label: 'Fossa', value: 'fossa' },
            ]"
        />
    </app-form-group>

    <app-form-group label="Anteversion mode">
        <app-toggle
            v-model="vm.specifications.cup.anteversion"
            data-testid="anteversion-mode-buttons"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('cupAnteversionMode') ? 'Please provide an option' : ''
            "
            :options="[
                { label: 'Combined', value: 'auto' },
                { label: 'Manual', value: 'manual' },
            ]"
        />
    </app-form-group>

    <app-form-group
        v-if="vm.specifications.cup.anteversion === 'manual'"
        label="Anteversion Angle"
        data-testid="anteversion-angle-input"
        input-id="cup-anteversion-angle"
        class="mt-12"
    >
        <app-text-input
            v-model="vm.specifications.cup.anteversionAngle"
            suffix="deg"
            input-id="cup-anteversion-angle"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('cupAnteversionAngle') ? anteversionOutOfRangeMessage : ''
            "
        />
    </app-form-group>

    <app-form-group label="Alignment mode" data-testid="alignment-mode-buttons">
        <app-toggle
            v-model="vm.specifications.cup.alignment"
            :disabled="areSurgeonSpecsDisabled"
            :error-messages="
                vm.showFieldError('alignmentMode') ? 'Please select an alignment mode' : ''
            "
            :options="alignmentModes()"
        />
    </app-form-group>
</template>
